import _ from 'lodash'

/**
 * Calculate subtotal for a booking based on pricing tiers and player categories
 *
 * @param {Object} options - Calculation options
 * @param {Array} options.pricing - Array of pricing tiers with min_players, max_players, price, category_id
 * @param {Array} options.categories - Array of player categories with category_id, number_of_players
 * @param {boolean} options.cumulativePricing - If true, use total player count to determine tier for all categories
 * @param {number} options.pricingTypeId - 1 = per person, 2 = flat rate
 * @returns {Object} { subtotal: number, numberOfPlayers: number }
 */
export function calculateSubtotal({ pricing, categories, cumulativePricing, pricingTypeId }) {
  let subtotal = 0
  let numberOfPlayers = 0

  // Calculate total player count across all categories
  for (const category of categories) {
    numberOfPlayers += Number(category.number_of_players) || 0
  }

  if (cumulativePricing) {
    if (pricingTypeId === 1) {
      // Per person pricing with cumulative tiers
      subtotal = calculateCumulativePerPerson(pricing, categories, numberOfPlayers)
    } else if (pricingTypeId === 2) {
      // Flat rate pricing with cumulative tiers
      subtotal = calculateCumulativeFlatRate(pricing, categories, numberOfPlayers)
    }
  } else {
    if (pricingTypeId === 1) {
      // Per person pricing with per-category tiers
      subtotal = calculatePerCategoryPerPerson(pricing, categories)
    } else if (pricingTypeId === 2) {
      // Flat rate pricing with per-category tiers
      subtotal = calculatePerCategoryFlatRate(pricing, categories)
    }
  }

  return { subtotal, numberOfPlayers }
}

/**
 * Apply the private-upgrade pricing mode to a booking's base subtotal.
 *
 * Upgrade-to-private has two modes (games.reservation_upgrade_pricing_mode):
 *   'buyout'           — flat fee replaces per-ticket pricing entirely
 *   'tickets_plus_fee' — normal per-ticket subtotal PLUS a flat nominal fee
 *
 * This is the single source of truth for upgrade pricing across every booking
 * surface (CartLine, CreateBookingView, Calendar, Bookings) AND the backend
 * (update-transaction-values, add-booking-to-transaction,
 * booking-site-make-booking-v2). Any change here MUST be mirrored in those
 * lambdas or FE/BE totals diverge and trip BookingInvalidTotalAttempts.
 *
 * @param {Object} options
 * @param {number} options.baseSubtotal - normal per-ticket subtotal (ignored in 'buyout')
 * @param {string} options.mode - 'buyout' | 'tickets_plus_fee' (defaults to 'buyout')
 * @param {number} options.fee - reservation_upgrade_fee (buyout amount or nominal surcharge)
 * @returns {number} the upgraded subtotal
 */
export function applyPrivateUpgrade({ baseSubtotal, mode, fee }) {
  const f = Number(fee) || 0
  if (mode === 'tickets_plus_fee') {
    return (Number(baseSubtotal) || 0) + f
  }
  return f // 'buyout' (default)
}

/**
 * Cumulative per-person pricing
 * Uses total player count to find tier, applies that tier's price to each category
 */
function calculateCumulativePerPerson(pricing, categories, totalPlayers) {
  let subtotal = 0

  for (const category of categories) {
    if (category.number_of_players <= 0) continue

    // Number() coercion at every category.price read — mysql2/promise
    // returns DECIMAL columns as strings, and `subtotal += "29.00"`
    // string-concatenates instead of summing.
    const catPrice = Number(category.price) || 0

    // Check if category has a custom price override
    if (category.hasOwnProperty('original_price')) {
      subtotal += catPrice * category.number_of_players
      continue
    }

    const categoryPricing = pricing.filter(p => p.category_id === category.category_id)
    let pricePerPerson = findPriceForPlayerCount(categoryPricing, totalPlayers)

    // Fallback to category's stored price if tier lookup returns 0
    if (pricePerPerson === 0 && catPrice > 0) {
      pricePerPerson = catPrice
    }

    subtotal += pricePerPerson * category.number_of_players
  }

  return subtotal
}

/**
 * Cumulative flat rate pricing
 * Uses total player count to find tier, applies flat rate once per category.
 * A category with no participants contributes nothing: the total headcount only
 * decides WHICH tier each populated category is charged at. Categories are
 * groups of participants in every pricing mode, so an empty category is never
 * sold (OTC-2911). Mirrors calculateCumulativePerPerson and the backend
 * ticket pricing helpers; any change here must be applied to those too.
 */
function calculateCumulativeFlatRate(pricing, categories, totalPlayers) {
  let subtotal = 0

  // Don't charge if no players at all
  if (totalPlayers <= 0) return 0

  for (const category of categories) {
    if (!(Number(category.number_of_players) > 0)) continue

    const catPrice = Number(category.price) || 0

    if (category.hasOwnProperty('original_price')) {
      subtotal += catPrice
      continue
    }

    const categoryPricing = pricing.filter(p => p.category_id === category.category_id)
    let flatRate = findPriceForPlayerCount(categoryPricing, totalPlayers)

    // Fallback to category's stored price if tier lookup returns 0
    if (flatRate === 0 && catPrice > 0) {
      flatRate = catPrice
    }

    subtotal += flatRate
  }

  return subtotal
}

/**
 * Per-category per-person pricing
 * Each category uses its own player count to determine tier
 */
function calculatePerCategoryPerPerson(pricing, categories) {
  let subtotal = 0

  for (const category of categories) {
    if (category.number_of_players <= 0) continue

    const catPrice = Number(category.price) || 0

    if (category.hasOwnProperty('original_price')) {
      subtotal += catPrice * category.number_of_players
      continue
    }

    const categoryPricing = pricing.filter(p => p.category_id === category.category_id)
    let pricePerPerson = findPriceForPlayerCount(categoryPricing, category.number_of_players)

    // Fallback to category's stored price if tier lookup returns 0
    if (pricePerPerson === 0 && catPrice > 0) {
      pricePerPerson = catPrice
    }

    subtotal += pricePerPerson * category.number_of_players
  }

  return subtotal
}

/**
 * Per-category flat rate pricing
 * Each category uses its own player count to determine tier, pays flat rate
 */
function calculatePerCategoryFlatRate(pricing, categories) {
  let subtotal = 0

  for (const category of categories) {
    if (category.number_of_players <= 0) continue

    const catPrice = Number(category.price) || 0

    if (category.hasOwnProperty('original_price')) {
      subtotal += catPrice
      continue
    }

    const categoryPricing = pricing.filter(p => p.category_id === category.category_id)
    let flatRate = findPriceForPlayerCount(categoryPricing, category.number_of_players)

    // Fallback to category's stored price if tier lookup returns 0
    if (flatRate === 0 && catPrice > 0) {
      flatRate = catPrice
    }

    subtotal += flatRate
  }

  return subtotal
}

/**
 * Find the price for a given player count from pricing tiers
 * Handles: within range, below minimum, above maximum
 */
export function findPriceForPlayerCount(categoryPricing, playerCount) {
  if (!categoryPricing || categoryPricing.length === 0) return 0

  // Sort by min_players to ensure correct order
  const sorted = _.sortBy(categoryPricing, 'min_players')

  // Coerce the matched tier's price to a Number before returning. MySQL
  // DECIMAL columns arrive as JS numbers from the legacy `mysql` driver
  // but as strings from `mysql2/promise` — without coercion here, callers
  // doing `subtotal += findPriceForPlayerCount(...)` string-concatenate
  // ("0" + "29.00" + "29.00" = "029.0029.00", parseFloat truncates to 29).
  // Coercing once at the source keeps every caller numerically safe
  // regardless of which mysql driver fed the data.
  const toNumber = (v) => Number(v) || 0

  // Find tier where playerCount falls within range
  for (const tier of sorted) {
    if (playerCount >= tier.min_players && playerCount <= tier.max_players) {
      return toNumber(tier.price)
    }
  }

  // Below minimum - use smallest tier
  const smallest = sorted[0]
  if (playerCount < smallest.min_players) {
    return toNumber(smallest.price)
  }

  // Above maximum - use largest tier
  const largest = sorted[sorted.length - 1]
  if (playerCount > largest.max_players) {
    return toNumber(largest.price)
  }

  return 0
}

/**
 * Safe rounding using exponential notation (avoids floating-point errors).
 *
 * Snaps a near-zero float residue to 0 before the exponential stringify.
 * A value that nets to mathematically-exactly-0 (e.g. a 100%-off promo on
 * inclusive-tax items) lands on a residue like -1.06e-14, whose String() is
 * scientific notation, so `value + 'e2'` becomes the invalid double-exponent
 * string '-1.06e-14e2' and Number(...) returns NaN. 0.005 is half a cent: a
 * real charge is always a multiple of 0.01, so no legitimate value is snapped.
 * Genuine NaN/Infinity is intentionally NOT swallowed — it passes through so
 * real bad-data bugs still surface instead of silently becoming 0.
 * Mirrors CreateBooking.vue (customer booking site) and the
 * update-transaction-values lambda.
 */
export function roundCents(value) {
  if (Math.abs(value) < 0.005) return 0
  return Number(Math.round(Number(value + 'e2')) + 'e-2')
}

/**
 * Convert a dollar value to integer cents using exponential notation.
 * Use this when comparing money values with `>`, `<`, `===` — integers
 * sidestep the FP traps that bite when summed dollar floats land on
 * values like 223.32999999999998. NaN propagates for invalid input,
 * so comparisons against missing/loading values stay silently false
 * (matches the prior `parseFloat(x)` behavior at call sites).
 */
export function toCents(value) {
  return Math.round(Number(parseFloat(value) + 'e2'))
}

function parseLinks(raw) {
  if (!raw) return null
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
  return Array.isArray(parsed) ? parsed : null
}

/**
 * A tax or fee applies to a line when it applies to everything, or its linked_games
 * (booking line), linked_items (item line) or linked_gift_card_types (gift card
 * line) names the line. A custom item has no catalog id, so only apply_to_all_items
 * rules reach it. Mirrors update-transaction-values getTaxItemLinks /
 * getFeeItemLinks (item_type game | item | gift_card_type).
 *
 * Booking lines keep the historical fallbacks (no gameId, or no linked_games on the
 * rule, means "applies"); item and gift card lines default-deny like the backend,
 * where a missing link row simply means not linked.
 */
function isRuleLinkedToLine(rule, { kind = null, gameId = null, itemId = null, giftCardTypeId = null } = {}) {
  if (rule.apply_to_all_items) return true
  if (kind === 'gift_card' || giftCardTypeId != null) {
    // Gift card line: one row per card on the backend, linked by its type.
    if (giftCardTypeId == null) return false
    const linkedTypes = parseLinks(rule.linked_gift_card_types)
    if (!linkedTypes) return false
    return !!_.find(linkedTypes, { id: Number(giftCardTypeId) })
  }
  if (kind === 'item' || itemId != null) {
    // Item line. A custom item (no catalog id) matches no link row on the backend.
    if (itemId == null) return false
    const linkedItems = parseLinks(rule.linked_items)
    if (!linkedItems) return false
    return !!_.find(linkedItems, { id: Number(itemId) })
  }
  if (!gameId) return true
  const linkedGames = parseLinks(rule.linked_games)
  if (!linkedGames) return true
  return !!_.find(linkedGames, { id: gameId })
}

/**
 * Check if a tax applies to the given game via linked_games filtering
 */
function isTaxLinkedToGame(tax, gameId) {
  return isRuleLinkedToLine(tax, { gameId })
}

function isFeeLinkedToGame(fee, gameId) {
  return isRuleLinkedToLine(fee, { gameId })
}

function inclusivePercentTaxes(taxes) {
  return (taxes || []).filter(t => t && t.pricing_type === 1 && (t.value_type === 2 || t.value_type === 4))
}

/**
 * Combined inclusive rate for the whole transaction: every inclusive percentage
 * tax that covers every line (value_type 2, or value_type 4 on all items).
 * Mirrors update-transaction-values buildInclusiveRateLookup.forTransaction.
 */
export function inclusiveRateForTransaction(taxes) {
  return inclusivePercentTaxes(taxes).reduce((rate, t) =>
    (t.value_type === 2 || t.apply_to_all_items) ? rate + (Number(t.amount) || 0) : rate, 0)
}

/**
 * Combined inclusive rate for one line: value_type 2 always, value_type 4 when it
 * applies to the line. A shelf price includes every inclusive tax at once, so each
 * tax extracts its base with this combined rate and then takes its own share;
 * dividing by its own rate alone overstates every tax once more than one is
 * inclusive. Mirrors buildInclusiveRateLookup.forLine.
 */
export function inclusiveRateForLine(taxes, line = {}) {
  return inclusivePercentTaxes(taxes).reduce((rate, t) =>
    (t.value_type === 2 || isRuleLinkedToLine(t, line)) ? rate + (Number(t.amount) || 0) : rate, 0)
}

/**
 * Per-line tax for the linked value types (1, 3, 4) when the caller prices a
 * multi-line order. `lines` are { lineId, kind, gameId, itemId, amount, quantity,
 * discount } where amount is the line's shelf total and discount the ex-tax
 * discount already attributed to that line. Mirrors update-transaction-values
 * calculateTotalTaxValue: per matching line, compound base taken once per tax,
 * and the backend's headroom rule: `remaining` (the taxable subtotal) shrinks per
 * matching line and a line charges 0 once it is negative. The backend takes its
 * inclusive branch whenever ANY inclusive tax exists; in that branch value type 4
 * is not headroom-gated, so `anyInclusive` switches the gate off for it.
 * Returns { amount, remaining }.
 */
function taxAmountForLines(tax, lines, priorTaxTotal, remaining, anyInclusive, rateForLine = null) {
  const amt = Number(tax.amount) || 0
  const rate = amt * 0.01
  let total = 0
  for (const line of lines) {
    if (!isRuleLinkedToLine(tax, line)) continue
    const lineDiscount = Number(line.discount) || 0
    if (tax.value_type === 1) {
      remaining -= amt
      total += remaining >= 0 ? amt * (Number(line.quantity) || 0) : 0
    } else if (tax.value_type === 3) {
      remaining -= amt
      total += remaining >= 0 ? amt : 0
    } else if (tax.value_type === 4) {
      let base
      if (tax.pricing_type === 1) {
        const inclusiveRate = (rateForLine ? rateForLine(line) : amt) * 0.01
        base = roundCents((Number(line.amount) || 0) / (1 + inclusiveRate))
      } else {
        base = Number(line.amount) || 0
      }
      if (tax.apply_after_discount) {
        base = roundCents(base - lineDiscount)
      }
      if (tax.is_compound) {
        base = base + priorTaxTotal
      }
      if (base < 0) base = 0
      const lineTax = roundCents(base * rate)
      if (anyInclusive) {
        remaining -= lineTax
        total += lineTax
      } else {
        remaining -= base * rate
        total += remaining >= 0 ? lineTax : 0
      }
    }
  }
  return { amount: roundCents(total), remaining }
}

/**
 * Calculate a single tax amount (for display of one tax line)
 * Mirrors backend update-transaction-values logic.
 * Caveat: the backend picks its inclusive branch for EVERY tax when any inclusive
 * tax exists on the group; an exclusive tax configured next to an inclusive one is
 * then base-extracted too. This helper branches per tax, so that mixed setup is
 * not mirrored (pre-existing).
 *
 * @param {Object} tax - Tax object from API
 * @param {number} subtotal - Booking subtotal
 * @param {number} numberOfPlayers - Total player count
 * @param {Object} [opts] - Optional: { discount, gameId, priorTaxTotal, inclusiveRate, lines, rateForLine }
 *   inclusiveRate is the combined inclusive percentage for the base extraction
 *   (see inclusiveRateForLine); it defaults to the tax's own rate, which is only
 *   right when it is the sole inclusive tax. rateForLine does the same per line.
 * @returns {number}
 */
export function calculateTaxAmount(tax, subtotal, numberOfPlayers, opts = {}) {
  if (!tax || subtotal <= 0) return 0
  const { discount = 0, gameId = null, priorTaxTotal = 0, lines = null, inclusiveRate = null, rateForLine = null } = opts

  // Multi-line order: the linked value types price per line (bookings and items).
  // Value types 0 and 2 stay transaction level on the combined subtotal below.
  // Headroom lives in calculateTaxBreakdown; a direct caller gets an unlimited remainder.
  if (Array.isArray(lines) && lines.length > 0 && (tax.value_type === 1 || tax.value_type === 3 || tax.value_type === 4)) {
    const anyInclusive = opts.anyInclusive === undefined ? tax.pricing_type === 1 : opts.anyInclusive
    const remaining = opts.remaining === undefined ? Number.POSITIVE_INFINITY : opts.remaining
    return taxAmountForLines(tax, lines, priorTaxTotal, remaining, anyInclusive, rateForLine).amount
  }

  // Linked taxes (value_type 1, 3, 4) must match the current game
  if ((tax.value_type === 1 || tax.value_type === 3 || tax.value_type === 4) && !isTaxLinkedToGame(tax, gameId)) {
    return 0
  }

  // value_type 0: flat per transaction
  if (tax.value_type === 0) {
    return Number(tax.amount) || 0
  }

  // value_type 1: flat per person (linked)
  if (tax.value_type === 1) {
    return (Number(tax.amount) || 0) * numberOfPlayers
  }

  // value_type 2: percentage per transaction
  if (tax.value_type === 2) {
    if (tax.pricing_type === 1) {
      // Inclusive: extract base first, then apply discount, then calculate tax
      let baseAmount = subtotal / (1 + ((inclusiveRate === null ? tax.amount : inclusiveRate) * 0.01))
      if (tax.apply_after_discount) {
        baseAmount = roundCents(baseAmount - discount)
      }
      if (tax.is_compound) {
        baseAmount = baseAmount + priorTaxTotal
      }
      if (baseAmount < 0) baseAmount = 0
      return roundCents(baseAmount * (tax.amount * 0.01))
    }
    // Exclusive
    let base = subtotal
    if (tax.apply_after_discount) {
      base = roundCents(base - discount)
    }
    if (tax.is_compound) {
      base = base + priorTaxTotal
    }
    if (base < 0) base = 0
    return roundCents(base * (tax.amount * 0.01))
  }

  // value_type 3: flat per line item (linked)
  if (tax.value_type === 3) {
    return Number(tax.amount) || 0
  }

  // value_type 4: percentage per line item (linked)
  if (tax.value_type === 4) {
    if (tax.pricing_type === 1) {
      // Inclusive: extract base first, then apply discount, then calculate tax
      let baseAmount = subtotal / (1 + ((inclusiveRate === null ? tax.amount : inclusiveRate) * 0.01))
      if (tax.apply_after_discount) {
        baseAmount = roundCents(baseAmount - discount)
      }
      if (tax.is_compound) {
        baseAmount = baseAmount + priorTaxTotal
      }
      if (baseAmount < 0) baseAmount = 0
      return roundCents(baseAmount * (tax.amount * 0.01))
    }
    // Exclusive
    let base = subtotal
    if (tax.apply_after_discount) {
      base = roundCents(base - discount)
    }
    if (tax.is_compound) {
      base = base + priorTaxTotal
    }
    if (base < 0) base = 0
    return roundCents(base * (tax.amount * 0.01))
  }

  // Fallback: use rate if value_type not set (older config)
  if (tax.rate !== undefined) {
    return roundCents(subtotal * (tax.rate / 100))
  }

  return 0
}

/**
 * Calculate all taxes and return per-tax breakdown + totals.
 * Mirrors backend update-transaction-values: sorts by is_compound ASC,
 * maintains a running totalTaxAmount for compound tax bases.
 *
 * @param {Object} opts
 * @param {Array} opts.taxes
 * @param {number} opts.subtotal
 * @param {number} opts.numberOfPlayers
 * @param {number} [opts.discount] - Total discount amount
 * @param {number} [opts.gameId] - Current game ID for linked tax filtering
 * @param {boolean} [opts.waiveTax]
 * @returns {Object} { totalTax, taxAmounts: [{ tax, amount }] }
 */
export function calculateTaxBreakdown({ taxes, subtotal, numberOfPlayers, discount = 0, gameId = null, waiveTax = false, lines = null }) {
  if (waiveTax || !taxes || taxes.length === 0) return { totalTax: 0, inclusiveTaxSavings: 0, taxAmounts: [] }
  // An empty lines array is treated as "no lines" so a caller that forgot the
  // booking line falls back to the single-line path instead of zeroing linked taxes.
  const hasLines = Array.isArray(lines) && lines.length > 0
  const undiscountedLines = hasLines ? lines.map(l => ({ ...l, discount: 0 })) : null
  const anyInclusive = taxes.some(t => t.pricing_type === 1)
  // Every inclusive percentage tax extracts its base with the combined inclusive
  // rate of the taxes sharing that base, then takes its own share.
  const rateForLine = hasLines ? (line => inclusiveRateForLine(taxes, line)) : null
  const inclusiveRateForTax = tax => {
    if (tax.pricing_type !== 1) return null
    return tax.value_type === 2 ? inclusiveRateForTransaction(taxes) : inclusiveRateForLine(taxes, { gameId })
  }
  // Backend headroom: the taxable subtotal shrinks as taxes are applied in order;
  // once negative, later gated taxes charge 0. Only applied on the lines path.
  let remaining = subtotal

  // Sort non-compound first, matching backend ORDER BY is_compound ASC
  const sorted = _.sortBy(taxes, t => t.is_compound ? 1 : 0)

  // Backend accumulates ALL tax amounts (inclusive + exclusive) into totalTaxAmount
  // for compound tax base calculations. Only exclusive taxes are added to the returned total.
  let totalTaxAmount = 0
  let exclusiveTaxTotal = 0
  let inclusiveTaxSavings = 0
  const taxAmounts = []

  for (const tax of sorted) {
    let amount
    if (hasLines && (tax.value_type === 1 || tax.value_type === 3 || tax.value_type === 4)) {
      const result = taxAmountForLines(tax, lines, totalTaxAmount, remaining, anyInclusive, rateForLine)
      amount = result.amount
      remaining = result.remaining
    } else {
      amount = calculateTaxAmount(tax, subtotal, numberOfPlayers, {
        discount,
        gameId,
        priorTaxTotal: totalTaxAmount,
        inclusiveRate: inclusiveRateForTax(tax)
      })
      if (hasLines) {
        // Transaction-level headroom, mirroring the backend per value type:
        // 0 is gated in both branches, 2 only in the exclusive branch.
        const amt = Number(tax.amount) || 0
        if (tax.value_type === 0) {
          remaining -= amt
          if (remaining < 0) amount = 0
        } else if (tax.value_type === 2) {
          if (anyInclusive) {
            remaining -= amount
          } else {
            let base = subtotal
            if (tax.apply_after_discount) base = roundCents(subtotal - discount)
            if (tax.is_compound) base = base + totalTaxAmount
            remaining -= base * (amt * 0.01)
            if (remaining < 0) amount = 0
          }
        }
      }
    }

    // For inclusive taxes with apply_after_discount, calculate the tax savings
    // (original tax - recalculated tax) so the total reflects the reduced price
    if (tax.pricing_type === 1 && tax.apply_after_discount && discount > 0
      && (tax.value_type === 2 || tax.value_type === 4)) {
      const originalAmount = hasLines && tax.value_type === 4
        ? taxAmountForLines(tax, undiscountedLines, totalTaxAmount - amount, Number.POSITIVE_INFINITY, anyInclusive, rateForLine).amount
        : calculateTaxAmount(tax, subtotal, numberOfPlayers, {
          discount: 0,
          gameId,
          priorTaxTotal: totalTaxAmount - amount,
          inclusiveRate: inclusiveRateForTax(tax)
        })
      inclusiveTaxSavings += originalAmount - amount
    }

    // All taxes accumulate into compound base (matching backend)
    totalTaxAmount += amount

    // Only exclusive taxes add to the displayed/charged total
    if (tax.pricing_type === 0) {
      exclusiveTaxTotal += amount
    }

    taxAmounts.push({ tax, amount })
  }

  return { totalTax: roundCents(exclusiveTaxTotal), inclusiveTaxSavings: roundCents(inclusiveTaxSavings), taxAmounts }
}

/**
 * Calculate total tax (exclusive only).
 * Convenience wrapper around calculateTaxBreakdown.
 *
 * @param {Array} taxes
 * @param {number} subtotal
 * @param {number} numberOfPlayers
 * @param {boolean} waiveTax
 * @param {Object} [opts] - Optional: { discount, gameId }
 * @returns {number}
 */
export function calculateTotalTax(taxes, subtotal, numberOfPlayers, waiveTax, opts = {}) {
  const { totalTax } = calculateTaxBreakdown({
    taxes, subtotal, numberOfPlayers, waiveTax,
    discount: opts.discount || 0,
    gameId: opts.gameId || null
  })
  return totalTax
}

/**
 * Calculate total fees
 * @param {Array} fees - Array of fee objects
 * @param {number} subtotal
 * @param {number} numberOfPlayers
 * @param {boolean} waiveFee
 * @returns {number}
 */
/**
 * Calculate a single fee amount
 * @param {Object} fee - Fee object
 * @param {number} subtotal
 * @param {number} numberOfPlayers
 * @param {Object} [opts]
 * @param {number} [opts.discount] - Shelf-price discount applied to this subtotal.
 *   Only consumed when fee.covered_by_promo_codes is truthy and the fee is
 *   percentage-based (value_type 2 or 4). Mirrors update-transaction-values:
 *   value_type 2 uses the transaction-level discount, value_type 4 uses the
 *   line-item discount. Flat fees (0, 1, 3) are unaffected by the toggle.
 * @returns {number}
 */
export function calculateFeeAmount(fee, subtotal, numberOfPlayers, opts = {}) {
  if (!fee) return 0
  const { discount = 0, gameId = null } = opts

  // Linked fees (value_type 1, 3, 4) must match the current game. value_type 0
  // (flat per transaction) and 2 (% per transaction) ignore linked_games —
  // mirrors backend update-transaction-values and customer-site CreateBooking.vue.
  if ((fee.value_type === 1 || fee.value_type === 3 || fee.value_type === 4) && !isFeeLinkedToGame(fee, gameId)) {
    return 0
  }

  // value_type: 0 = flat, 1 = per person, 2 = percentage, 3 = flat linked, 4 = percentage linked
  if (fee.value_type === 0 || fee.value_type === 3) {
    // Backend ("flat per line item") returns 0 when there's no chargeable
    // subtotal — mirror that here so the UI total matches the saved transaction.
    if (subtotal <= 0) return 0
    return roundCents(fee.amount)
  } else if (fee.value_type === 1) {
    return roundCents(fee.amount * numberOfPlayers)
  } else if (fee.value_type === 2 || fee.value_type === 4) {
    let base = subtotal
    if (fee.covered_by_promo_codes) {
      base = roundCents(subtotal - discount)
      if (base < 0) base = 0
    }
    return roundCents(base * (fee.amount / 100))
  }
  return 0
}

/**
 * One fee against a multi-line order, with the backend's headroom rule.
 * update-transaction-values calculateTotalFeeValue keeps `remainingSubtotal`
 * across fees in order and, for value types 0, 2, 3 and 4, decrements it by the
 * fee before testing `>= 0`; a negative remainder means the fee is 0. Value
 * type 1 (per person) charges regardless of headroom.
 * Returns { amount, remaining }.
 */
function feeAmountWithLines(fee, subtotal, lines, opts, remaining) {
  const { discount = 0 } = opts
  const amt = Number(fee.amount) || 0
  const rate = amt / 100
  let amount = 0
  if (fee.value_type === 0) {
    remaining -= amt
    amount = remaining >= 0 ? roundCents(amt) : 0
  } else if (fee.value_type === 1) {
    for (const line of lines) {
      if (!isRuleLinkedToLine(fee, line)) continue
      remaining -= amt
      amount += amt * (Number(line.quantity) || 0)
    }
    amount = roundCents(amount)
  } else if (fee.value_type === 2) {
    let base = subtotal
    if (fee.covered_by_promo_codes) {
      base = roundCents(subtotal - discount)
    }
    remaining -= base * rate
    amount = remaining >= 0 ? roundCents(base * rate) : 0
    if (amount < 0) amount = 0
  } else if (fee.value_type === 3) {
    for (const line of lines) {
      if (!isRuleLinkedToLine(fee, line)) continue
      remaining -= amt
      amount += remaining >= 0 ? amt : 0
    }
    amount = roundCents(amount)
  } else if (fee.value_type === 4) {
    for (const line of lines) {
      if (!isRuleLinkedToLine(fee, line)) continue
      let base = Number(line.amount) || 0
      if (fee.covered_by_promo_codes) {
        base = roundCents(base - (Number(line.discount) || 0))
      }
      remaining -= base * rate
      const lineFee = remaining >= 0 ? roundCents(base * rate) : 0
      amount += lineFee > 0 ? lineFee : 0
    }
    amount = roundCents(amount)
  }
  return { amount, remaining }
}

/**
 * Every fee's amount for the order, in fee order, plus the total. Same math as
 * calculateTotalFee (which delegates here); surfaces that list each fee read
 * `feeAmounts` so the rows always sum to the total they show. Waived fees come
 * back as zero rows so lookups by fee id never miss.
 *
 * @returns {{ totalFee: number, feeAmounts: Array<{ id: *, name: string, amount: number }> }}
 */
export function calculateFeeBreakdown(fees, subtotal, numberOfPlayers, waiveFee, opts = {}) {
  const list = Array.isArray(fees) ? fees : []
  if (waiveFee || list.length === 0) {
    return { totalFee: 0, feeAmounts: list.map(fee => ({ id: fee.id, name: fee.name, amount: 0 })) }
  }
  const feeAmounts = []
  let total = 0
  if (Array.isArray(opts.lines) && opts.lines.length > 0) {
    // Multi-line order: fees run in order against a shrinking remainder, as the backend does.
    let remaining = subtotal
    for (const fee of list) {
      const result = feeAmountWithLines(fee, subtotal, opts.lines, opts, remaining)
      feeAmounts.push({ id: fee.id, name: fee.name, amount: result.amount })
      total += result.amount
      remaining = result.remaining
    }
    return { totalFee: roundCents(total), feeAmounts }
  }
  for (const fee of list) {
    const amount = calculateFeeAmount(fee, subtotal, numberOfPlayers, opts)
    feeAmounts.push({ id: fee.id, name: fee.name, amount })
    total += amount
  }
  return { totalFee: roundCents(total), feeAmounts }
}

export function calculateTotalFee(fees, subtotal, numberOfPlayers, waiveFee, opts = {}) {
  return calculateFeeBreakdown(fees, subtotal, numberOfPlayers, waiveFee, opts).totalFee
}

/**
 * Face value of an event gift card whose type includes taxes and fees.
 *
 * The card is sold for the event's price; with include_taxes_and_fees on, the
 * value also covers the exclusive taxes and the fees that booking will carry so
 * the recipient's booking is covered in full. Nothing here taxes the gift card
 * purchase itself. Linked rules (value types 1, 3, 4) honour the game link when
 * the rule carries one; inclusive taxes are already inside the price and only
 * feed a compound base.
 *
 * @param {Object} p
 * @param {number|string} p.subtotal - Event price for the chosen player counts
 * @param {Array} p.taxes - Group taxes (pricing_type, value_type, amount, links)
 * @param {Array} p.fees - Group fees
 * @param {number} p.numberOfPlayers - Players baked into the card
 * @param {number|null} p.gameId - The linked event
 * @returns {number} subtotal + exclusive taxes + fees, in cents-rounded dollars
 */
export function calculateEventGiftCardValue({ subtotal, taxes = [], fees = [], numberOfPlayers = 0, gameId = null }) {
  const base = roundCents(Number(subtotal) || 0)
  const tax = calculateTotalTax(taxes || [], base, numberOfPlayers, false, { gameId })
  const fee = calculateTotalFee(fees || [], base, numberOfPlayers, false, { gameId })
  return roundCents(base + tax + fee)
}

/**
 * Extract the base price for discount calculation, matching backend getBasePrice logic.
 * Only extracts tax for inclusive taxes with apply_after_discount enabled.
 * When apply_after_discount is off, returns the shelf price.
 */
export function getBasePrice(price, taxes, gameId = null, itemId = null) {
  if (!taxes || taxes.length === 0) return price
  const hasInclusive = taxes.some(t => t.pricing_type === 1)
  if (!hasInclusive) return price

  let combinedRate = 0
  for (const tax of taxes) {
    if (tax.pricing_type !== 1) continue
    if (tax.value_type !== 2 && tax.value_type !== 4) continue
    if (!tax.apply_after_discount) continue

    let applies = false
    if (tax.apply_to_all_items) {
      applies = true
    } else if (itemId != null) {
      applies = isRuleLinkedToLine(tax, { itemId })
    } else {
      const linked = parseLinks(tax.linked_games)
      applies = !!(linked && _.find(linked, { id: gameId }))
    }

    if (applies) {
      combinedRate += tax.amount
    }
  }

  if (combinedRate === 0) return price
  return roundCents(price / (1 + (combinedRate * 0.01)))
}

/**
 * Calculate discount for a single promo code against a SINGLE line's subtotal.
 *
 * Per-line semantics: backend `update-transaction-values` rounds the discount
 * per cart line and sums (Math.round(price × value_amount) + 'e-2' per line).
 * Summing first then rounding drifts by ~½¢ per line on fractional results.
 *
 * For single-booking surfaces (BookingDrawer/CreateBookingView), the caller's
 * `subtotal` IS one line and this function rounds once correctly.
 *
 * For multi-line carts (NewTransaction), do NOT call this with a combined
 * subtotal — loop the cart and call once per line, then sum. See
 * `NewTransaction.vue` `getDiscount`/`getShelfDiscount` for the pattern.
 *
 * @param {Object} promo - Promo object with value_type, redemption_type, value_amount
 * @param {number} subtotal - One cart line's price (NOT a multi-line sum)
 * @param {number} numberOfPlayers - Player count for value_type=2 (per-quantity)
 * @param {Object} [opts] - Optional: { taxes, gameId }
 * @returns {{ exTaxDiscount: number, shelfDiscount: number }}
 */
export function calculateDiscount(promo, subtotal, numberOfPlayers, opts = {}) {
  if (!promo) return { exTaxDiscount: 0, shelfDiscount: 0 }
  let exTaxDiscount = 0
  let shelfDiscount = 0
  const basePrice = getBasePrice(subtotal, opts.taxes, opts.gameId)

  // value_type: 0 or 1 = per-transaction, 2 = per-quantity
  // redemption_type: 1 = flat amount, 0 = percentage
  if (promo.value_type === 0 || promo.value_type === 1) {
    if (promo.redemption_type === 1) {
      // Flat: shelf price is the stated amount, ex-tax is converted via getBasePrice
      shelfDiscount = promo.value_amount
      if (shelfDiscount > subtotal) shelfDiscount = subtotal
      exTaxDiscount = getBasePrice(shelfDiscount, opts.taxes, opts.gameId)
    } else {
      // Percentage: computed from base price (already ex-tax), shelf is from original price
      exTaxDiscount = Number(Math.round(basePrice * promo.value_amount) + 'e-2')
      shelfDiscount = Number(Math.round(subtotal * promo.value_amount) + 'e-2')
    }
  } else if (promo.value_type === 2) {
    if (promo.redemption_type === 1) {
      // Flat per quantity
      shelfDiscount = promo.value_amount * numberOfPlayers
      if (shelfDiscount > subtotal) shelfDiscount = subtotal
      exTaxDiscount = getBasePrice(shelfDiscount, opts.taxes, opts.gameId)
    } else {
      // Percentage per quantity
      exTaxDiscount = Number(Math.round(basePrice * promo.value_amount * numberOfPlayers) + 'e-2')
      shelfDiscount = Number(Math.round(subtotal * promo.value_amount * numberOfPlayers) + 'e-2')
    }
  }

  return { exTaxDiscount: roundCents(exTaxDiscount), shelfDiscount: roundCents(shelfDiscount) }
}

/**
 * Calculate total discounts from all promo codes against a SINGLE line.
 *
 * Same single-line constraint as `calculateDiscount`. For multi-line carts,
 * loop the cart yourself and sum per-line results — do NOT pass a combined
 * subtotal here or percentage promos will drift vs. backend.
 *
 * @param {Array} promos
 * @param {number} subtotal - One cart line's price (NOT a multi-line sum)
 * @param {number} numberOfPlayers
 * @param {Object} [opts] - Optional: { taxes, gameId }
 * @returns {{ exTaxDiscount: number, shelfDiscount: number }}
 */
export function calculateTotalDiscount(promos, subtotal, numberOfPlayers, opts = {}) {
  if (!promos || promos.length === 0) return { exTaxDiscount: 0, shelfDiscount: 0 }
  let totalExTax = 0
  let totalShelf = 0
  for (const promo of promos) {
    const result = calculateDiscount(promo, subtotal, numberOfPlayers, opts)
    totalExTax += result.exTaxDiscount
    totalShelf += result.shelfDiscount
  }
  if (totalExTax > subtotal) totalExTax = subtotal
  if (totalShelf > subtotal) totalShelf = subtotal
  return { exTaxDiscount: roundCents(totalExTax), shelfDiscount: roundCents(totalShelf) }
}

/**
 * Promo discounts for a multi-line order (booking, item and gift card lines),
 * mirroring update-transaction-values calculateDiscounts for value types 0, 1 and 2.
 *
 * Lines: { lineId, kind: 'booking' | 'item' | 'gift_card', gameId, itemId,
 * giftCardTypeId, amount, quantity }. A gift card line is one card (the backend
 * reads one gift_cards row per card, quantity 1, price = the card's list price),
 * so a purchase of N cards is N lines.
 * Eligibility: `is_cross_group`, or promo.games names the booking's gameId, or
 * promo.items names the item's itemId, or promo.gift_card_types names the card's
 * giftCardTypeId. A booking or gift card line is also eligible when the promo
 * object carries no `games` / `gift_card_types` at all (those payloads were
 * pre-filtered by game or card type before reaching here). An item line defaults
 * to not eligible; a custom item (no itemId) is eligible only through is_cross_group.
 * Gift cards are never taxed, so their ex-tax discount equals the shelf discount.
 *
 * value_type 0 (per transaction): per-line amounts are summed, clamped to the
 * whole order's subtotal, then redistributed across the eligible lines in
 * items-then-bookings-then-gift-cards order, proportional to line amount,
 * remainder on the last eligible line (the backend keeps the redistributed
 * shares, not the per-line percent, and those shares feed per-line taxes and fees).
 * value_type 1: per eligible line. value_type 2: per eligible line times quantity.
 * No per-line clamp: the caller clamps the grand total to the subtotal, as the
 * backend handler does.
 *
 * @returns {{ exTaxDiscount: number, shelfDiscount: number, perLine: Map<string, {exTax: number, shelf: number}> }}
 */
export function calculateLineDiscounts({ promos = [], lines = [], taxes = [] } = {}) {
  const perLine = new Map()
  const add = (lineId, exTax, shelf) => {
    const cur = perLine.get(lineId) || { exTax: 0, shelf: 0 }
    perLine.set(lineId, { exTax: roundCents(cur.exTax + exTax), shelf: roundCents(cur.shelf + shelf) })
  }
  const cents = (x) => Number(Math.round(x) + 'e-2')
  // Backend all_items order: items, bookings, gift cards.
  const ordered = [
    ...lines.filter(l => l.kind === 'item'),
    ...lines.filter(l => l.kind !== 'item' && l.kind !== 'gift_card'),
    ...lines.filter(l => l.kind === 'gift_card')
  ]
  const orderSubtotal = roundCents(lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0))
  let exTaxDiscount = 0
  let shelfDiscount = 0

  const isEligible = (promo, line) => {
    if (promo.is_cross_group) return true
    if (line.kind === 'item') {
      if (line.itemId == null) return false
      const items = parseLinks(promo.items)
      return !!(items && _.find(items, { id: Number(line.itemId) }))
    }
    if (line.kind === 'gift_card') {
      if (promo.gift_card_types === undefined) return true
      const types = parseLinks(promo.gift_card_types)
      return !!(types && _.find(types, { id: Number(line.giftCardTypeId) }))
    }
    if (promo.games === undefined) return true
    const games = parseLinks(promo.games)
    return !!(games && _.find(games, { id: Number(line.gameId) }))
  }
  // Ex-tax value of a shelf amount on this line. Gift cards carry no tax, so the
  // backend never extracts one from them (propExTax = propShelf).
  const exTaxOf = (line, amount) => line.kind === 'gift_card'
    ? roundCents(amount)
    : getBasePrice(amount, taxes, line.gameId || null, line.itemId || null)
  const baseOf = (line) => exTaxOf(line, Number(line.amount) || 0)

  for (const promo of promos) {
    if (!promo) continue
    const v = Number(promo.value_amount) || 0
    const flat = promo.redemption_type === 1
    const eligible = ordered.filter(l => isEligible(promo, l))
    if (eligible.length === 0) continue

    if (promo.value_type === 0) {
      let shelfTotal = 0
      let exTaxTotal = 0
      if (flat) {
        shelfTotal = Math.min(v, orderSubtotal)
      } else {
        for (const line of eligible) {
          shelfTotal += cents((Number(line.amount) || 0) * v)
          exTaxTotal += cents(baseOf(line) * v)
        }
        shelfTotal = Math.min(roundCents(shelfTotal), orderSubtotal)
        exTaxTotal = Math.min(roundCents(exTaxTotal), orderSubtotal)
      }
      const eligibleTotal = eligible.reduce((sum, l) => sum + (Number(l.amount) || 0), 0)
      let distributedShelf = 0
      let distributedExTax = 0
      eligible.forEach((line, idx) => {
        const last = idx === eligible.length - 1
        const amount = Number(line.amount) || 0
        let propShelf = 0
        let propExTax = 0
        if (eligibleTotal > 0) {
          propShelf = last ? roundCents(shelfTotal - distributedShelf) : roundCents((amount / eligibleTotal) * shelfTotal)
          if (flat) {
            propExTax = exTaxOf(line, propShelf)
          } else {
            propExTax = last ? roundCents(exTaxTotal - distributedExTax) : roundCents((amount / eligibleTotal) * exTaxTotal)
          }
        }
        distributedShelf = roundCents(distributedShelf + propShelf)
        distributedExTax = roundCents(distributedExTax + propExTax)
        add(line.lineId, propExTax, propShelf)
      })
      exTaxDiscount += distributedExTax
      shelfDiscount += distributedShelf
    } else if (promo.value_type === 1) {
      for (const line of eligible) {
        const amount = Number(line.amount) || 0
        const shelf = flat ? v : cents(amount * v)
        const exTax = flat ? exTaxOf(line, v) : cents(baseOf(line) * v)
        add(line.lineId, exTax, shelf)
        exTaxDiscount += exTax
        shelfDiscount += shelf
      }
    } else if (promo.value_type === 2) {
      for (const line of eligible) {
        const amount = Number(line.amount) || 0
        const qty = Number(line.quantity) || 0
        let shelf, exTax
        if (flat) {
          shelf = roundCents(v * qty)
          exTax = roundCents(exTaxOf(line, v) * qty)
        } else {
          shelf = cents(amount * v * qty)
          exTax = cents(baseOf(line) * v * qty)
        }
        add(line.lineId, exTax, shelf)
        exTaxDiscount += exTax
        shelfDiscount += shelf
      }
    }
  }

  return { exTaxDiscount: roundCents(exTaxDiscount), shelfDiscount: roundCents(shelfDiscount), perLine }
}

/**
 * Distribute a dollar total across N units in cent-accurate amounts.
 * Mirrors backend booking-site-validate-promo-code:655 `splitAmount`.
 * Returns an array of N dollar values that sum exactly to `total`
 * (within roundCents tolerance), with extra pennies pushed to the
 * first entries. Used when expanding a booking into per-ticket units
 * for BXGY discount calculation.
 *
 * @param {number} total - Total dollar amount to distribute
 * @param {number} count - Number of units to split into
 * @returns {number[]} Array of dollar values
 */
export function splitAmount(total, count) {
  if (count <= 0) return []
  const cents = Math.round(Number(total + 'e2'))
  const base = Math.floor(cents / count)
  const remainder = cents % count
  const out = []
  for (let i = 0; i < count; i++) {
    const unitCents = base + (i < remainder ? 1 : 0)
    out.push(Number(unitCents + 'e-2'))
  }
  return out
}

/**
 * Calculate the combined BUY_X_GET_Y discount for all applicable promos
 * against a cart. Mirrors backend `calculateBuyXGetYDiscount` semantics in
 * `booking-site-validate-promo-code` and `update-transaction-values`.
 *
 * Supports per-side `unit` ('booking' | 'ticket') on `required_items` and
 * `discounted_items`. When a side opts into 'ticket', bookings are expanded
 * into per-participant pool entries via `splitAmount` for cent-accuracy.
 * Mixed configs are supported — X-side and Y-side are evaluated independently.
 *
 * Algorithm per BXGY rule:
 *   1. Build a unified pool of cart units (bookings as 1 entry or N entries
 *      depending on whichever side opts into 'ticket'; items/gift cards
 *      always per-quantity).
 *   2. Consume highest-value matching units for the X requirement.
 *   3. From remaining pool, select cheapest matching units for the Y discount
 *      (cheapest-first preserves the X-side's high-value consumption).
 *   4. Apply the configured discount type (free / percentage / fixed) up to
 *      `max_quantity` units.
 *   5. For per-booking Y-units, consume all expanded ticket entries of that
 *      booking together; for per-ticket Y-units, consume one ticket at a time.
 *
 * Per-line attribution: returns a `perLine` map keyed by `cartLineId` so
 * callers can show which booking received how much discount.
 *
 * @param {Object} opts
 * @param {Array} opts.promos - Promo objects, each may carry a `rules` array
 * @param {Array} opts.cart - Cart bookings: { id, game_id, total, group_size }
 * @param {Array} [opts.items=[]] - Merchandise: { id, item_id, amount, quantity }
 * @param {Array} [opts.giftCards=[]] - Gift cards: { id, gift_card_type_id, total, quantity }
 * @param {Array} [opts.taxes=[]] - Tax list for inclusive-tax base extraction
 * @returns {{ exTaxDiscount: number, shelfDiscount: number, perLine: Map<any, {exTax:number,shelf:number}>, appliedRules: Array }}
 */
export function calculateBuyXGetYDiscount({ promos, cart = [], items = [], giftCards = [], taxes = [] }) {
  const result = { exTaxDiscount: 0, shelfDiscount: 0, perLine: new Map(), appliedRules: [] }
  if (!promos || promos.length === 0) return result

  for (const promo of promos) {
    const rules = promo.rules || []
    const bxgyRules = rules.filter(r => r.rule_type && r.rule_type.rule_key === 'BUY_X_GET_Y')
    if (bxgyRules.length === 0) continue

    for (const rule of bxgyRules) {
      const config = typeof rule.rule_config === 'string'
        ? JSON.parse(rule.rule_config)
        : rule.rule_config
      if (!config) continue

      const { exTax, shelf, perLine } = evaluateBuyXGetYRule(config, cart, items, giftCards, taxes)
      if (shelf <= 0) continue

      result.exTaxDiscount = roundCents(result.exTaxDiscount + exTax)
      result.shelfDiscount = roundCents(result.shelfDiscount + shelf)
      for (const [lineId, amounts] of perLine) {
        const existing = result.perLine.get(lineId) || { exTax: 0, shelf: 0 }
        result.perLine.set(lineId, {
          exTax: roundCents(existing.exTax + amounts.exTax),
          shelf: roundCents(existing.shelf + amounts.shelf)
        })
      }
      result.appliedRules.push({ promoId: promo.id, ruleId: rule.id, exTax, shelf })
    }
  }

  return result
}

/**
 * Evaluate a single BUY_X_GET_Y rule against the cart. Internal helper for
 * `calculateBuyXGetYDiscount`. See that function's docblock for semantics.
 */
function evaluateBuyXGetYRule(config, cart, items, giftCards, taxes) {
  const empty = { exTax: 0, shelf: 0, perLine: new Map() }
  if (!config || !config.required_items || !config.discounted_items) return empty

  const req = config.required_items
  const disc = config.discounted_items
  const xUnit = req.unit === 'ticket' ? 'ticket' : 'booking'
  const yUnit = disc.unit === 'ticket' ? 'ticket' : 'booking'
  const expandBookings = xUnit === 'ticket' || yUnit === 'ticket'

  const pool = buildBxgyPool(cart, items, giftCards, taxes, expandBookings)
  if (pool.length === 0) return empty

  // X consumption sorts by shelf value (most expensive consumed first, preserving cheaper for Y).
  const xConsumed = consumeXUnits(pool, req, xUnit)
  if (!xConsumed.satisfied) return empty

  const ySelection = selectYUnits(xConsumed.remaining, disc, yUnit)
  if (ySelection.length === 0) return empty

  const perLine = new Map()
  let exTaxTotal = 0
  let shelfTotal = 0
  for (const unit of ySelection) {
    const exTaxAmt = computeYUnitDiscount(unit.baseValue, disc.discount_type, disc.discount_value)
    const shelfAmt = computeYUnitDiscount(unit.shelfValue, disc.discount_type, disc.discount_value)
    if (shelfAmt <= 0) continue
    exTaxTotal += exTaxAmt
    shelfTotal += shelfAmt
    const lineId = unit.originalRef && unit.originalRef.cartLineId
    if (lineId !== undefined) {
      const existing = perLine.get(lineId) || { exTax: 0, shelf: 0 }
      perLine.set(lineId, {
        exTax: roundCents(existing.exTax + exTaxAmt),
        shelf: roundCents(existing.shelf + shelfAmt)
      })
    }
  }

  return { exTax: roundCents(exTaxTotal), shelf: roundCents(shelfTotal), perLine }
}

/**
 * Build the BXGY evaluation pool. Each entry: { type, id, value, originalRef }.
 * Bookings split into per-ticket units when `expandBookings` is true, using
 * splitAmount for cent-accurate distribution; items/gift cards always per-unit.
 */
function buildBxgyPool(cart, items, giftCards, taxes, expandBookings) {
  const pool = []

  for (const booking of cart) {
    const groupSize = Math.max(1, Number(booking.group_size || booking.quantity || 1))
    const lineShelf = Number(booking.total || booking.value || 0)
    const lineBase = getBasePrice(lineShelf, taxes, booking.game_id)
    const cartLineId = booking.id != null ? booking.id : booking.game_id

    if (expandBookings) {
      const shelfPer = splitAmount(lineShelf, groupSize)
      const basePer = splitAmount(lineBase, groupSize)
      for (let i = 0; i < groupSize; i++) {
        pool.push({
          type: 'games',
          id: booking.game_id,
          shelfValue: shelfPer[i],
          baseValue: basePer[i],
          originalRef: { source: 'booking', cartLineId, ticketIndex: i, groupSize }
        })
      }
    } else {
      pool.push({
        type: 'games',
        id: booking.game_id,
        shelfValue: lineShelf,
        baseValue: lineBase,
        originalRef: { source: 'booking', cartLineId, ticketIndex: null, groupSize }
      })
    }
  }

  for (const item of items) {
    const qty = Math.max(1, Number(item.quantity || 1))
    const lineShelf = Number(item.amount != null ? item.amount : item.value || 0)
    const lineBase = getBasePrice(lineShelf, taxes, null)
    const shelfPer = splitAmount(lineShelf, qty)
    const basePer = splitAmount(lineBase, qty)
    const cartLineId = item.id != null ? item.id : `item-${item.item_id}`
    for (let i = 0; i < qty; i++) {
      pool.push({
        type: 'items',
        id: item.item_id,
        shelfValue: shelfPer[i],
        baseValue: basePer[i],
        originalRef: { source: 'item', cartLineId }
      })
    }
  }

  for (const gc of giftCards) {
    const qty = Math.max(1, Number(gc.quantity || 1))
    const lineShelf = Number(gc.total != null ? gc.total : gc.value || 0)
    // Gift cards don't carry inclusive tax — base === shelf
    const shelfPer = splitAmount(lineShelf, qty)
    const cartLineId = gc.id != null ? gc.id : `gc-${gc.gift_card_type_id}`
    for (let i = 0; i < qty; i++) {
      pool.push({
        type: 'gift_cards',
        id: gc.gift_card_type_id,
        shelfValue: shelfPer[i],
        baseValue: shelfPer[i],
        originalRef: { source: 'gift_card', cartLineId }
      })
    }
  }

  return pool
}

/**
 * Consume units from the pool to satisfy the X requirement.
 * In 'ticket' mode each pool entry counts as 1.
 * In 'booking' mode for games, all expanded tickets of a booking are
 * consumed together (so groupSize tickets remove from the pool per X-count).
 * Returns { satisfied, remaining }.
 */
function consumeXUnits(pool, req, xUnit) {
  const xMinQty = Math.max(1, Number(req.min_quantity || 1))
  const xItemType = req.item_type
  const xItemIds = (req.item_ids || []).map(Number)
  const xMatchMode = req.match_mode || 'any'

  // Sort by shelf value desc — consume highest-value units first
  const sortedPool = [...pool].sort((a, b) => b.shelfValue - a.shelfValue)

  const matches = (poolItem) => {
    const typeOk = xItemType === 'any' || poolItem.type === xItemType
    const idOk = !xItemIds.length || xItemIds.includes(Number(poolItem.id))
    return typeOk && idOk
  }

  // For 'all' match_mode (only meaningful when specific item IDs are set),
  // require at least one unit from EACH listed ID. Simplification: still consume
  // xMinQty total, but fail if not every listed ID has at least one matching unit.
  if (xMatchMode === 'all' && xItemIds.length > 1) {
    const presentIds = new Set(sortedPool.filter(p => matches(p)).map(p => Number(p.id)))
    for (const requiredId of xItemIds) {
      if (!presentIds.has(requiredId)) return { satisfied: false, remaining: pool }
    }
  }

  const remaining = []
  let xConsumed = 0
  const consumedBookingLineIds = new Set()

  for (const poolItem of sortedPool) {
    if (xConsumed >= xMinQty) {
      remaining.push(poolItem)
      continue
    }

    const isBookingUnit = poolItem.type === 'games' && poolItem.originalRef.source === 'booking'

    if (matches(poolItem)) {
      if (xUnit === 'ticket' || !isBookingUnit) {
        xConsumed++
        continue
      }
      // booking-mode X on a games line: consume the entire booking's tickets
      // (or just this one if pool isn't expanded) as ONE X-unit.
      const lineId = poolItem.originalRef.cartLineId
      if (!consumedBookingLineIds.has(lineId)) {
        consumedBookingLineIds.add(lineId)
        xConsumed++
      }
      // Drop this pool entry regardless (consumed as part of the booking).
      continue
    }

    remaining.push(poolItem)
  }

  if (xConsumed < xMinQty) return { satisfied: false, remaining: pool }

  // If X was booking-mode, also drop any expanded tickets that survived
  // the first pass because they were sorted past the consumed booking.
  const finalRemaining = xUnit === 'booking'
    ? remaining.filter(p => !(p.originalRef.source === 'booking' && consumedBookingLineIds.has(p.originalRef.cartLineId)))
    : remaining

  return { satisfied: true, remaining: finalRemaining }
}

/**
 * Select Y units to discount from the remaining pool.
 * In 'ticket' mode: pick up to max_quantity cheapest matching ticket-entries.
 * In 'booking' mode for games: pick up to max_quantity bookings (cheapest first
 * by booking total), and the unit's `value` is the full booking total.
 */
function selectYUnits(remaining, disc, yUnit) {
  const yItemType = disc.item_type
  const yItemIds = (disc.item_ids || []).map(Number)
  const maxQty = Math.max(1, Number(disc.max_quantity || 1))

  const matches = (poolItem) => {
    const typeOk = poolItem.type === yItemType
    const idOk = yItemIds.length > 0 && yItemIds.includes(Number(poolItem.id))
    return typeOk && idOk
  }

  if (yUnit === 'ticket' || yItemType !== 'games') {
    const eligible = remaining.filter(matches).sort((a, b) => a.shelfValue - b.shelfValue)
    return eligible.slice(0, maxQty)
  }

  // booking-mode Y on games: collapse expanded tickets back into per-booking units
  const byBooking = new Map()
  for (const poolItem of remaining) {
    if (!matches(poolItem)) continue
    if (poolItem.originalRef.source !== 'booking') continue
    const lineId = poolItem.originalRef.cartLineId
    const existing = byBooking.get(lineId)
    if (existing) {
      existing.shelfValue = roundCents(existing.shelfValue + poolItem.shelfValue)
      existing.baseValue = roundCents(existing.baseValue + poolItem.baseValue)
    } else {
      byBooking.set(lineId, {
        type: 'games',
        id: poolItem.id,
        shelfValue: poolItem.shelfValue,
        baseValue: poolItem.baseValue,
        originalRef: { ...poolItem.originalRef, ticketIndex: null }
      })
    }
  }
  const bookingUnits = Array.from(byBooking.values()).sort((a, b) => a.shelfValue - b.shelfValue)
  return bookingUnits.slice(0, maxQty)
}

/**
 * Apply the discount type to a single unit's value. Returns dollar discount.
 */
function computeYUnitDiscount(unitValue, discountType, discountValue) {
  if (unitValue <= 0) return 0
  if (discountType === 'free') return roundCents(unitValue)
  if (discountType === 'percentage') {
    const pct = Number(discountValue) / 100
    return roundCents(unitValue * pct)
  }
  if (discountType === 'fixed_amount' || discountType === 'fixed') {
    return roundCents(Math.min(Number(discountValue), unitValue))
  }
  return 0
}

/**
 * Calculate the combined BUNDLE_PRICE ("buy N for $X") discount for all
 * applicable promos against a cart. Mirrors the authoritative core in
 * `update-transaction-values` `calculateBundlePriceDiscount` (also pinned by
 * tests/bundle-price.test.mjs): highest-priced units selected first, three
 * price modes, optional reapplication in full groups, per-line cap at the
 * line's own value, integer-cents math throughout.
 *
 * ELIGIBILITY CONTRACT: every line passed in is treated as qualifying. The
 * backends filter by the promo's linked items themselves; this preview cannot
 * (the console search response only carries game links), so the CALLER must
 * pass only eligible lines. CreateBookingView satisfies this by construction:
 * its promo search default-denies promos that do not link the cart's game,
 * and its preview cart is that single vetted booking.
 *
 * Rule dollars are booked with shelf equal to ex-tax (zero inclusive tax
 * savings), matching how the recompute authority accumulates them, so both
 * returned totals are the base-price amount.
 *
 * @param {Object} opts
 * @param {Array} opts.promos - Promo objects, each may carry a `rules` array
 * @param {Array} opts.cart - Bookings: { id, game_id, total, group_size }
 * @param {Array} [opts.items=[]] - Merchandise: { id, item_id, amount, quantity }
 * @param {Array} [opts.giftCards=[]] - Gift cards: { id, gift_card_type_id, total, quantity }
 * @param {Array} [opts.taxes=[]] - Tax list for inclusive-tax base extraction
 * @returns {{ exTaxDiscount: number, shelfDiscount: number, perLine: Map<any, {exTax:number,shelf:number}>, appliedRules: Array }}
 */
export function calculateBundlePriceDiscount({ promos, cart = [], items = [], giftCards = [], taxes = [] }) {
  const result = { exTaxDiscount: 0, shelfDiscount: 0, perLine: new Map(), appliedRules: [] }
  if (!promos || promos.length === 0) return result

  for (const promo of promos) {
    const rules = promo.rules || []
    const bundleRules = rules.filter(r => r.rule_type && r.rule_type.rule_key === 'BUNDLE_PRICE')
    if (bundleRules.length === 0) continue

    for (const rule of bundleRules) {
      const config = typeof rule.rule_config === 'string'
        ? JSON.parse(rule.rule_config)
        : rule.rule_config
      if (!config) continue

      const { amount, perLine } = evaluateBundlePriceRule(config, cart, items, giftCards, taxes)
      if (amount <= 0) continue

      result.exTaxDiscount = roundCents(result.exTaxDiscount + amount)
      result.shelfDiscount = roundCents(result.shelfDiscount + amount)
      for (const [lineId, amounts] of perLine) {
        const existing = result.perLine.get(lineId) || { exTax: 0, shelf: 0 }
        result.perLine.set(lineId, {
          exTax: roundCents(existing.exTax + amounts.exTax),
          shelf: roundCents(existing.shelf + amounts.shelf)
        })
      }
      result.appliedRules.push({ promoId: promo.id, ruleId: rule.id, exTax: amount, shelf: amount })
    }
  }

  return result
}

/**
 * Evaluate a single BUNDLE_PRICE rule. Internal helper for
 * `calculateBundlePriceDiscount`; CORE mirrors the lambda implementations.
 */
function evaluateBundlePriceRule(config, cart, items, giftCards, taxes) {
  const empty = { amount: 0, perLine: new Map() }
  const N = Number(config.min_quantity) || 0
  const M = Number(config.affected_quantity) || 0
  const Xc = Math.round(Number((Number(config.bundle_price) || 0) + 'e2'))
  const mode = config.price_mode
  const reapply = !!config.allow_reapplication

  if (M <= 0) return empty
  if (mode !== 'cart_total' && mode !== 'line_item' && mode !== 'quantity') return empty

  const cents = (v) => Math.round(Number(((v != null ? v : 0)) + 'e2'))

  // Build qualifying lines: bookings are one unit each (no ticket expansion in
  // bundle math, matching the authoritative core); items/gift cards carry
  // their quantity. Values are ex-tax base cents.
  const lines = []
  for (const booking of cart) {
    const shelf = Number(booking.total || booking.value || 0)
    const c = cents(getBasePrice(shelf, taxes, booking.game_id))
    const lineId = booking.id != null ? booking.id : booking.game_id
    lines.push({ lineId, qty: 1, lineCents: c, unitCents: c })
  }
  for (const item of items) {
    const qty = Math.max(1, Number(item.quantity || 1))
    const shelf = Number(item.amount != null ? item.amount : item.value || 0)
    const c = cents(getBasePrice(shelf, taxes, null))
    const lineId = item.id != null ? item.id : `item-${item.item_id}`
    lines.push({ lineId, qty, lineCents: c, unitCents: Math.round(c / qty) })
  }
  for (const gc of giftCards) {
    const qty = Math.max(1, Number(gc.quantity || 1))
    const c = cents(Number(gc.total != null ? gc.total : gc.value || 0))
    const lineId = gc.id != null ? gc.id : `gc-${gc.gift_card_type_id}`
    lines.push({ lineId, qty, lineCents: c, unitCents: Math.round(c / qty) })
  }

  const totalUnits = lines.reduce((s, l) => s + l.qty, 0)
  const totalCount = mode === 'line_item' ? lines.length : totalUnits
  if (totalCount < N) return empty

  const byLine = new Map()
  const addDisc = (line, c) => {
    if (c <= 0) return
    const prev = byLine.get(line.lineId)
    byLine.set(line.lineId, { lineCents: line.lineCents, cents: (prev ? prev.cents : 0) + c })
  }

  if (mode === 'line_item') {
    const sorted = [...lines].sort((a, b) => b.lineCents - a.lineCents)
    const affected = reapply ? Math.floor(sorted.length / M) * M : Math.min(M, sorted.length)
    for (let i = 0; i < affected; i++) addDisc(sorted[i], sorted[i].lineCents - Xc)
  } else {
    const units = []
    for (const l of lines) for (let i = 0; i < l.qty; i++) units.push({ line: l, valueCents: l.unitCents })
    units.sort((a, b) => b.valueCents - a.valueCents)
    const affectedCount = reapply ? Math.floor(units.length / M) * M : Math.min(M, units.length)
    const affected = units.slice(0, affectedCount)

    if (mode === 'quantity') {
      for (const u of affected) addDisc(u.line, u.valueCents - Xc)
    } else {
      for (let g = 0; g < affected.length; g += M) {
        const group = affected.slice(g, g + M)
        if (group.length < M) continue
        const sumCents = group.reduce((s, u) => s + u.valueCents, 0)
        const groupDiscount = sumCents - Xc
        if (groupDiscount <= 0) continue
        let remaining = groupDiscount
        for (let k = 0; k < group.length; k++) {
          const share = (k === group.length - 1)
            ? remaining
            : Math.min(remaining, Math.round(groupDiscount * (group[k].valueCents / sumCents)))
          remaining -= share
          addDisc(group[k].line, share)
        }
      }
    }
  }

  let totalCents = 0
  const perLine = new Map()
  for (const [lineId, v] of byLine) {
    const capped = Math.min(v.cents, v.lineCents)
    if (capped <= 0) continue
    totalCents += capped
    const dollars = Number(capped + 'e-2')
    perLine.set(lineId, { exTax: dollars, shelf: dollars })
  }
  return { amount: Number(totalCents + 'e-2'), perLine }
}

/**
 * Calculate the final booking total
 * @param {Object} opts
 * @param {number} opts.subtotal
 * @param {Array} opts.taxes
 * @param {Array} opts.fees
 * @param {Array} opts.promos
 * @param {number} opts.numberOfPlayers
 * @param {boolean} opts.waiveTax
 * @param {boolean} opts.waiveFee
 * @returns {Object} { subtotal, totalTax, totalFee, totalDiscount, total }
 */
export function calculateTotal({ subtotal, taxes, fees, promos, numberOfPlayers, waiveTax, waiveFee, gameId = null }) {
  const discountResult = calculateTotalDiscount(promos, subtotal, numberOfPlayers, { taxes, gameId })
  const exTaxDiscount = discountResult.exTaxDiscount
  const shelfDiscount = discountResult.shelfDiscount

  // Pass ex-tax discount to tax calculation so inclusive tax base is computed correctly
  const { totalTax } = calculateTaxBreakdown({
    taxes, subtotal, numberOfPlayers, waiveTax,
    discount: exTaxDiscount, gameId
  })
  const totalFee = calculateTotalFee(fees, subtotal, numberOfPlayers, waiveFee, { discount: shelfDiscount, gameId })

  // Reconcile: for inclusive tax, shelf discount = ex-tax discount + tax savings.
  // Using shelf discount directly avoids penny rounding drift.
  let reconciledSavings = roundCents(shelfDiscount - exTaxDiscount)
  if (reconciledSavings < 0) reconciledSavings = 0

  let total = roundCents(subtotal + totalTax + totalFee - exTaxDiscount - reconciledSavings)
  if (total < 0) total = 0

  // Return shelf-price discount for customer-facing display
  return { subtotal, totalTax, totalFee, totalDiscount: shelfDiscount, total }
}

/**
 * Compute the expected deposit for a single booking, mirroring the customer
 * booking-site math in backend `booking-site-make-booking-v2.isDepositValid`.
 *
 * deposit_type 1 = per-quantity (deposit_amount * numberOfPlayers)
 * Anything else (0, null, undefined) is flat. Legacy games may have NULL
 * deposit_type even when allow_deposit + deposit_amount are set.
 *
 * @param {Object} pricing - resolved pricing object with allow_deposit,
 *   require_deposit, deposit_type, deposit_amount (matches BookingDrawer's
 *   drawerData.value.pricing shape)
 * @param {number} numberOfPlayers - total players across all categories
 * @returns {number} expected deposit in dollars, rounded to cents. 0 when
 *   deposits are disabled or the amount is invalid.
 */
export function calculateExpectedDeposit(pricing, numberOfPlayers) {
  if (!pricing || !pricing.allow_deposit) return 0
  const amount = parseFloat(pricing.deposit_amount)
  if (!isFinite(amount) || amount <= 0) return 0

  if (pricing.deposit_type === 1) {
    const players = parseInt(numberOfPlayers, 10) || 0
    if (players <= 0) return 0
    return roundCents(amount * players)
  }
  return roundCents(amount)
}

// ─────────────────────────────────────────────────────────────────────────────
// Cart-level pricing (for multi-booking transactions)
//
// These functions orchestrate the existing per-line functions across a cart of
// bookings (+ items + gift cards). They mirror the backend's
// update-transaction-values lambda so FE and backend totals stay in sync.
//
// Design principle: cart-level functions DELEGATE to the existing per-line
// functions. They don't re-implement subtotal/tax/fee/discount math. They just
// loop, allocate, and sum. This keeps a single source of truth for per-line
// behavior and means a per-line bug fix benefits both consumers (single-
// booking drawer flow + multi-booking review flow).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sum the cart's bookings, items, and gift cards into a multi-component
 * subtotal. Mirrors `update-transaction-values.computeCartSubtotals` (lambda
 * lines 263-290): no rounding inside the loop, values are taken as-is from
 * pre-rounded inputs. Accumulation order (items → bookings → gift cards)
 * also matches the lambda so float drift is bit-exact across the boundary.
 *
 * Field naming note: the lambda reads `bookings[n].price` (the persisted
 * booking_slots.price after the lambda has aggregated tickets server-side).
 * The FE's in-progress cart carries per-booking aggregates under `.subtotal`
 * (what existing `calculateSubtotal` returns). To accept either shape, we
 * fall back `.subtotal ?? .price ?? 0`. If a booking is in the cart without
 * either field, that's a staging bug — we warn but coerce to 0 to keep the
 * UI alive.
 *
 * Inputs (each defaults to []):
 *   bookings  — each has `subtotal` (FE staged) or `price` (persisted)
 *   items     — each has a numeric `amount` (price × quantity, pre-discount)
 *   giftCards — each has a numeric `price`
 *
 * Returns:
 *   subtotal           — all three categories summed
 *   taxableSubtotal    — items + bookings (gift cards aren't taxable, see
 *                        feedback_pricing_giftcard_discount)
 *   totalItemPrice     — items only
 *   totalBookingPrice  — bookings only
 *   totalGiftCardPrice — gift cards only
 *
 * @param {Object} input
 * @param {Array}  input.bookings - must be an Array (not an object with
 *   numeric keys). Each booking should have `subtotal` (FE) or `price` (DB).
 * @param {Array}  input.items
 * @param {Array}  input.giftCards
 * @returns {{
 *   subtotal: number,
 *   taxableSubtotal: number,
 *   totalItemPrice: number,
 *   totalBookingPrice: number,
 *   totalGiftCardPrice: number
 * }}
 */
export function calculateCartSubtotal({ bookings = [], items = [], giftCards = [] } = {}) {
  let totalItemPrice = 0
  let totalBookingPrice = 0
  let totalGiftCardPrice = 0

  for (const i of items) {
    totalItemPrice += Number(i?.amount) || 0
  }
  for (const b of bookings) {
    const value = b?.subtotal ?? b?.price
    if (value == null && b != null) {
      console.warn('calculateCartSubtotal: booking missing subtotal/price', b)
    }
    totalBookingPrice += Number(value) || 0
  }
  for (const g of giftCards) {
    totalGiftCardPrice += Number(g?.price) || 0
  }

  const subtotal = totalItemPrice + totalBookingPrice + totalGiftCardPrice
  const taxableSubtotal = totalItemPrice + totalBookingPrice

  return {
    subtotal,
    taxableSubtotal,
    totalItemPrice,
    totalBookingPrice,
    totalGiftCardPrice
  }
}


/**
 * Multiple durations. A pricing row (tier joined to its category) carries
 * game_duration_id: null for the default duration, else the duration's id.
 * Keeps only the rows for the selected duration; null means the default.
 */
export function filterPricingByDuration(rows, selectedDurationId) {
  const list = Array.isArray(rows) ? rows : []
  if (selectedDurationId === null || selectedDurationId === undefined) {
    return list.filter(r => r.game_duration_id === null || r.game_duration_id === undefined)
  }
  return list.filter(r => Number(r.game_duration_id) === Number(selectedDurationId))
}

function parseRows(value) {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value.length) {
    try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : [] } catch (e) { return [] }
  }
  return []
}

/**
 * Duration settings for a slot row as the console list lambdas return it
 * (companies-get-available-bookings: slot_durations, game_durations,
 * enable_multiple_durations, game_enable_multiple_durations). Custom pricing on
 * means the slot owns its durations; off means the event does. JSON strings are parsed.
 * Returns { enabled, durations }; enabled is false when the list is empty.
 */
export function slotDurationSettings(slot) {
  const s = slot || {}
  const isCustom = Number(s.is_custom_pricing_enabled) === 1 || s.is_custom_pricing_enabled === true
  const durations = parseRows(isCustom ? s.slot_durations : s.game_durations)
  const enabled = Number(isCustom ? s.enable_multiple_durations : s.game_enable_multiple_durations) === 1
  return { enabled: enabled && durations.length > 0, durations }
}

/**
 * The one display order for durations on every surface: ascending by minutes,
 * the default marked by its label rather than moved to the front.
 */
export function sortDurations(durations) {
  return [...(Array.isArray(durations) ? durations : [])]
    .sort((a, b) => (Number(a.duration_minutes) - Number(b.duration_minutes)) || (Number(a.id) - Number(b.id)))
}

/**
 * Pill options for a duration picker: the default duration maps to null (the
 * value the settlement lambdas read as "default"), every other to its id.
 */
export function durationPickerOptions(durations) {
  return sortDurations(durations).map(d => ({
    value: Number(d.is_default) === 1 ? null : Number(d.id),
    label: `${d.duration_minutes} min${Number(d.is_default) === 1 ? ' (default)' : ''}`
  }))
}

/**
 * The pricing rows a slot sells under the selected duration (null = default).
 * Custom pricing on: the slot's custom_pricing rows. Off: all_pricing (every
 * duration's rows, tagged) when the lambda sent it, else the default-only
 * pricing / game_pricing. Rows without a game_duration_id tag count as default.
 */
export function slotPricingRows(slot, selectedDurationId) {
  const s = slot || {}
  const isCustom = Number(s.is_custom_pricing_enabled) === 1 || s.is_custom_pricing_enabled === true
  let rows
  if (isCustom) {
    rows = parseRows(s.custom_pricing)
  } else {
    const all = parseRows(s.all_pricing)
    rows = all.length ? all : parseRows(s.pricing !== undefined ? s.pricing : s.game_pricing)
  }
  return filterPricingByDuration(rows, selectedDurationId === undefined ? null : selectedDurationId)
}

/**
 * Splits a flat category list (each category tagged with game_duration_id) into
 * the default duration's categories and one array per non-default duration,
 * keyed by duration_minutes. `durations` is the API list [{id, duration_minutes, is_default}].
 */
export function splitCategoriesByDuration(categories, durations) {
  const list = Array.isArray(categories) ? categories : []
  const minutesById = new Map((Array.isArray(durations) ? durations : []).map(d => [Number(d.id), Number(d.duration_minutes)]))
  const defaultCategories = []
  const byMinutes = new Map()
  for (const cat of list) {
    const durationId = cat.game_duration_id
    if (durationId === null || durationId === undefined || !minutesById.has(Number(durationId))) {
      defaultCategories.push(cat)
      continue
    }
    const minutes = minutesById.get(Number(durationId))
    if (!byMinutes.has(minutes)) byMinutes.set(minutes, [])
    byMinutes.get(minutes).push(cat)
  }
  return { defaultCategories, byMinutes }
}

/**
 * Inverse of splitCategoriesByDuration for save payloads: the default set gets
 * duration_minutes null, every other set gets its duration's minutes.
 * `durationSets` is [{ duration_minutes, is_default, pricing_categories }];
 * the default entry's categories are ignored (the default set is `defaultCategories`).
 * Editor-only keys (errs, error_*) are stripped.
 */
export function flattenCategoriesByDuration(defaultCategories, durationSets) {
  const clean = (cat, minutes) => {
    const out = {}
    for (const key of Object.keys(cat || {})) {
      if (key === 'errs' || key.startsWith('error_')) continue
      out[key] = cat[key]
    }
    out.duration_minutes = minutes
    if (Array.isArray(out.pricing)) {
      out.pricing = out.pricing.map(tier => {
        const t = {}
        for (const key of Object.keys(tier || {})) {
          if (key.startsWith('error_')) continue
          t[key] = tier[key]
        }
        return t
      })
    }
    return out
  }
  const flat = (Array.isArray(defaultCategories) ? defaultCategories : []).map(cat => clean(cat, null))
  for (const set of (Array.isArray(durationSets) ? durationSets : [])) {
    if (set.is_default) continue
    for (const cat of (set.pricing_categories || [])) {
      flat.push(clean(cat, Number(set.duration_minutes)))
    }
  }
  return flat
}
